/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { ActionManager, ActionManagerFn } from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionDownloadAttachment';

const ActionDownloadAttachmentManager: ActionManagerFn<
  'ActionDownloadAttachment'
> = (platform: Platform): ActionManager<'ActionDownloadAttachment'> => {
  return {
    ...baseActionManager(platform, 'ActionDownloadAttachment'),
    defaultValue,
    props: {
      ...baseActionProps,
      attachmentId: {
        label: 'Attachment Id',
        type: 'expression',
        returnType: async () => 'string',
      },
      attachmentVersion: {
        label: 'Attachment Version Id',
        type: 'expression',
        returnType: async () => 'string',
      },
    },
    name: 'Download Attachment',
    getDisplayText: (item) => item.props.attachmentId?.value,
    icon: { iconName: 'file-download' },
    description: `**Overview**<br/>
The Download Attachment action is a file management component designed to initiate the download of a specific file stored within the application's backend. It functions by referencing a unique identifier for the attachment, allowing users to retrieve documents, images, or other media directly to their local device.<br/><br/>
This component supports retrieving specific versions of a file, making it suitable for systems that implement version control for documents. It leverages expression-based properties for its inputs, enabling dynamic retrieval where the file ID is determined at runtime (e.g., based on the currently selected row in a grid).

**Key Features**<br/>
* **Dynamic File Resolution:** Uses an expression property for the attachmentId, allowing the action to download different files based on variable contexts (e.g., #{selectedRow.fileId}).
* **Version Control Support:** Includes an optional attachmentVersion property, enabling the retrieval of a specific historical version of a file rather than just the latest iteration.
* **Visual Context:** The editor displays the value of the attachmentId property directly in the action list (getDisplayText), helping developers identify which file logic is being configured.

**Properties**<br/>
* **Attachment Id:** An expression property (returning a string) that specifies the unique identifier of the file to download.
* **Attachment Version Id:** An expression property (returning a string) that specifies the version hash or ID of the file, if a specific version is required.

**Use Cases**<br/>
* **Document Management:** Clicking a "Download" icon in a list of invoices to retrieve the specific PDF associated with that record.
* **Audit Trails:** Retrieving a specific past version of a contract or agreement using the attachmentVersion property to verify what was signed at a specific time.`,
  };
};

export default ActionDownloadAttachmentManager;
